// ES module (see shared/i18n/index.js's own header comment). Strings used
// only by portal/ (buyer request form) — split out of the old monolithic
// shared/i18n/strings.js so admin/ stops shipping them (admin has no reason
// to bundle guest-OTP or catalog-browse copy); see shared/i18n/common.js's
// header comment for the full story and its sibling shared/i18n/admin.js
// for the other half.
//
// Composed with shared/i18n/common.js by portal/src/i18n.js. Every id below
// keeps its ORIGINAL value unchanged from strings.js — this is a pure move,
// not a rewrite. The Amharic strings were authored by a build agent (no
// native-speaker review pass yet) — see PROGRESS.md Phase 4's "Review
// Amharic strings" item, still open.

const strings = {
  // --- Errors mapped from API error codes — guest/account-holder endpoints
  // only (portal/src/utils/errorMapping.js's mapApiErrorId, and portal's own
  // LoginPage.jsx). Admin never calls these endpoints, so it never receives
  // these codes — see shared/i18n/admin.js for the disjoint set of codes
  // admin/sales endpoints can throw. -------------------------------------
  'error.INVALID_CREDENTIALS': {
    am: 'ኢሜይል ወይም የይለፍ ቃል ትክክል አይደለም።',
    en: 'Invalid email or password.',
  },
  'error.ACCOUNT_INACTIVE': {
    am: 'ይህ መለያ ንቁ አይደለም።',
    en: 'This account is inactive.',
  },
  'error.NO_ORGANIZATION': {
    am: 'መለያዎ ገና ከድርጅት ጋር አልተገናኘም።',
    en: 'Your account is not linked to an organization yet.',
  },
  'error.EMPTY_REQUEST': {
    am: 'ቢያንስ አንድ ዕቃ ያስፈልጋል።',
    en: 'At least one item is required.',
  },
  'error.OTP_NOT_REQUESTED': {
    am: 'ለዚህ ኢሜይል ገቢር ኮድ የለም። እባክዎ አዲስ ኮድ ይላኩ።',
    en: 'No active code for this email. Please request a new one.',
  },
  'error.OTP_INCORRECT': {
    am: 'ያስገቡት ኮድ ትክክል አይደለም። እባክዎ እንደገና ይሞክሩ።',
    en: 'That code is incorrect. Please try again.',
  },
  'error.OTP_TOO_MANY_ATTEMPTS': {
    am: 'በጣም ብዙ ትክክል ያልሆኑ ሙከራዎች ተደርገዋል። እባክዎ አዲስ ኮድ ይጠይቁ።',
    en: 'Too many incorrect attempts. Please request a new code.',
  },
  'error.OTP_NOT_VERIFIED': {
    am: 'የኢሜይል ማረጋገጫዎ ጊዜው አልፎበታል። እባክዎ እንደገና ያረጋግጡ።',
    en: 'Your email verification has expired. Please verify again.',
  },
  // --- Customer-accounts task: registration + password reset error codes.
  'error.DUPLICATE_EMAIL': {
    am: 'ይህ ኢሜይል አስቀድሞ ተመዝግቧል። ይልቁን ይግቡ ወይም የይለፍ ቃል ዳግም ያስጀምሩ።',
    en: 'That email is already registered. Try signing in, or reset your password.',
  },
  'error.REGISTRATION_NOT_VERIFIED': {
    am: 'የኢሜይል ማረጋገጫዎ ጊዜው አልፎበታል። እባክዎ እንደገና ያረጋግጡ።',
    en: 'Your email verification has expired. Please verify again.',
  },
  'error.PASSWORD_RESET_RATE_LIMITED': {
    am: 'በጣም ብዙ ጥያቄዎች። እባክዎ ቆየት ብለው እንደገና ይሞክሩ።',
    en: 'Too many requests. Try again later.',
  },
  'error.PASSWORD_RESET_TOKEN_NOT_FOUND': {
    am: 'ይህ አገናኝ ልክ አይደለም። እባክዎ አዲስ የዳግም ማስጀመሪያ አገናኝ ይጠይቁ።',
    en: 'This link is invalid. Please request a new reset link.',
  },
  'error.PASSWORD_RESET_TOKEN_EXPIRED': {
    am: 'ይህ አገናኝ ጊዜው አልፎበታል። እባክዎ አዲስ የዳግም ማስጀመሪያ አገናኝ ይጠይቁ።',
    en: 'This link has expired. Please request a new reset link.',
  },
  'error.TRACKING_TOKEN_EXPIRED': {
    am: 'ይህ አገናኝ ጊዜው አልፎበታል።',
    en: 'This link has expired.',
  },
  'error.TRACKING_TOKEN_NOT_FOUND': {
    am: 'ከዚህ የጥያቄ ቁጥር እና ኢሜይል ጋር የሚዛመድ ጥያቄ አልተገኘም።',
    en: 'No request matches that reference number and email.',
  },
  'error.TRACKING_LOOKUP_RATE_LIMITED': {
    am: 'ለዚህ የጥያቄ ቁጥር በጣም ብዙ ሙከራዎች። ትንሽ ቆይተው እንደገና ይሞክሩ።',
    en: 'Too many attempts for this reference number. Try again in a little while.',
  },
  // OTP verification gate in front of GET /track/:token (migration 022) —
  // components/TrackingGate.jsx. REQUIRED itself is never actually shown as
  // an error banner (ProformaPage.jsx branches on it to render the gate
  // instead), but it is a real code the API can return to any OTHER gated
  // tracking route (accept/decline/review/notes/...) if a page is left open
  // past the gate's expiry, so it still needs a translation.
  'error.TRACKING_VERIFICATION_REQUIRED': {
    am: 'ይህን ፕሮፎርማ ለማየት መጀመሪያ ኢሜይልዎን ያረጋግጡ።',
    en: 'Please verify your email before viewing this proforma.',
  },
  'error.TRACKING_VERIFICATION_RATE_LIMITED': {
    am: 'በጣም ብዙ ሙከራዎች። ትንሽ ቆይተው እንደገና ይሞክሩ።',
    en: 'Too many attempts. Try again in a little while.',
  },
  'error.QUOTATION_NOT_REVIEWABLE': {
    am: 'ይህ ፕሮፎርማ አሁን ለግምገማ ዝግጁ አይደለም።',
    en: 'This proforma is not ready to review right now.',
  },
  'error.INVALID_LINE_DECISION': {
    am: 'ለእያንዳንዱ መስመር ትክክለኛ ውሳኔ ያስፈልጋል።',
    en: 'A valid decision for every line is required.',
  },
  'error.EMPTY_NOTE': {
    am: 'ማስታወሻ ባዶ መሆን አይችልም።',
    en: 'A note cannot be empty.',
  },
  'error.QUOTATION_NOT_REQUOTABLE': {
    am: 'ለዚህ ጥያቄ አዲስ ዋጋ መጠየቅ አይቻልም።',
    en: 'Updated prices cannot be requested for this request right now.',
  },
  // Sealed-bid tenders (migration 020): the UI should already gate access
  // and other buyer state-changing actions before they can ever reach a
  // still-sealed quotation, so this is a defensive, rarely-hit path (e.g. a
  // stale page open when a scheduled unseal fires mid-session) — the
  // wording stays calm, matching proformaDoc.sealed.* in common.js, not
  // alarming.
  'error.QUOTATION_SEALED': {
    am: 'ይህ ፕሮፎርማ አሁንም የታሸገ ነው እና ገና ሊከፈት አይችልም።',
    en: 'This proforma is still sealed and cannot be opened yet.',
  },

  // --- portal/ (buyer request form) ---------------------------------------
  // Landing: two choices; the hero itself has no account-creation link (the
  // guest flow, /start, needs no account at all — portal/CLAUDE.md: "the
  // guest path must never require an account"). Registration/sign-in live
  // one level down, in components/SiteHeader.jsx and pages/LoginPage.jsx —
  // see 'portal.registerEmail.*'/'portal.login.registerLink' below
  // (customer-accounts task; the original "admin creates users" note here
  // was about STAFF accounts, sales/admin, which is still true and
  // unrelated to buyer self-registration).
  'portal.landing.title': { am: 'ለ ስነ ኢትኤል እንኳን ደህና መጡ', en: 'Welcome to SineEthel' },
  'portal.landing.subtitle': {
    am: 'ምርቶችን ይጠይቁ፣ የተፈረመ ፕሮፎርማ ይቀበሉ።',
    en: 'Request products, receive a signed proforma.',
  },
  // Hero's two actions (Direction B desktop redesign, entry 41) — replaces
  // the old "continue as guest / I have an account" framing; signing in is
  // now a small link in components/SiteHeader.jsx instead (reuses
  // 'portal.login.title', not a new key). Both hero buttons currently route
  // to the same real guest flow (/start) — 'portal.landing.uploadBoq' is a
  // deliberate placeholder: the actual BOQ/Excel upload+mapping screen is
  // still an unbuilt PROGRESS.md item, so this button is honestly routed
  // (not a dead link) but doesn't yet show an upload widget once there.
  'portal.landing.sendRequest': { am: 'ጥያቄ ላክ', en: 'Send a request' },
  'portal.landing.uploadBoq': { am: 'BOQ ወይም PDF ስቀል', en: 'Upload BOQ or PDF' },
  'portal.landing.categoriesTitle': { am: 'በምድብ ይግዙ', en: 'Shop by category' },
  // Hero search box (catalog-detail task) — submits to /catalog?q=<term>,
  // which CatalogBrowsePage.jsx seeds its own search state from on arrival.
  'portal.landing.searchPlaceholder': {
    am: 'ላፕቶፖች፣ ማተሚያዎች፣ ቶነር ይፈልጉ...',
    en: 'Search laptops, printers, toner…',
  },
  'portal.landing.searchSubmit': { am: 'ፈልግ', en: 'Search' },
  // Bottom of the featured-products grid, links to /catalog (the full
  // browse screen, mounted at a second path alongside /browse — see
  // App.jsx). The arrow is baked into both languages' copy, not appended
  // separately, so it reads naturally in either direction.
  'portal.landing.viewFullCatalog': { am: 'ሙሉ ካታሎግ ይዩ →', en: 'View full catalog →' },

  // Login (account holder). Kept separate from admin/'s own 'login.*'
  // strings — same field labels in spirit, but this app's copy/context is
  // buyer-facing, not "sign in to the sales workspace."
  'portal.login.title': { am: 'ግባ', en: 'Sign in' },
  'portal.login.subtitle': {
    am: 'የመለያ ኢሜይልዎን እና የይለፍ ቃልዎን ያስገቡ።',
    en: 'Enter your account email and password.',
  },
  'portal.login.email': { am: 'ኢሜይል', en: 'Email' },
  'portal.login.password': { am: 'የይለፍ ቃል', en: 'Password' },
  'portal.login.submit': { am: 'ግባ', en: 'Sign in' },
  'portal.login.submitting': { am: 'በመግባት ላይ...', en: 'Signing in...' },
  'portal.login.notBuyerAccount.title': {
    am: 'ይህ የገዥ መለያ አይደለም',
    en: 'This is not a buyer account',
  },
  'portal.login.notBuyerAccount.body': {
    am: 'ይህ መለያ ለ ስነ ኢትኤል ሠራተኞች የተያዘ ነው። እባክዎ የገዥ መለያ በመጠቀም ይግቡ ወይም እንደ እንግዳ ይቀጥሉ።',
    en: 'This account belongs to SineEthel staff. Please sign in with a buyer account, or continue as a guest.',
  },
  'portal.login.backToLanding': { am: '← ተመለስ', en: '← Back' },
  'portal.login.tryAgain': { am: 'ሌላ መለያ ሞክር', en: 'Try a different account' },
  'portal.login.tryAgainClearing': { am: 'በማጽዳት ላይ...', en: 'Clearing session...' },
  // Customer-accounts task: links off the login form. A first-time buyer
  // needs a way off this screen — registration and the no-account guest
  // path are both surfaced here, not only on the landing page.
  'portal.login.forgotPassword': { am: 'የይለፍ ቃል ረሱ?', en: 'Forgot your password?' },
  'portal.login.noAccountPrompt': { am: 'መለያ የለዎትም?', en: "Don't have an account?" },
  'portal.login.registerLink': { am: 'ይመዝገቡ', en: 'Register' },
  'portal.login.guestLink': { am: 'እንደ እንግዳ ይቀጥሉ', en: 'Continue as guest' },
  // Login-dead-end task: a guest with no account who lands here looking for
  // an EXISTING request needs a way out that isn't "start a new one" — the
  // only escape hatch this screen offered before (guestLink, above) goes to
  // /start. This pairs with it, going to /track instead.
  'portal.login.trackExistingLink': {
    am: 'ያለ ጥያቄዎን ይከትሉ',
    en: 'Looking for an existing request? Track it here',
  },

  // Guest step 1/3: email, POST /guest/otp/request.
  'portal.guestEmail.title': { am: 'ኢሜይልዎን ያረጋግጡ', en: 'Verify your email' },
  'portal.guestEmail.subtitle': {
    am: 'የማረጋገጫ ኮድ ወደ ኢሜይልዎ እንልካለን።',
    en: "We'll send a verification code to your email.",
  },
  'portal.guestEmail.label': { am: 'የኢሜይል አድራሻ', en: 'Email address' },
  'portal.guestEmail.submit': { am: 'ኮድ ላክ', en: 'Send code' },
  'portal.guestEmail.submitting': { am: 'በመላክ ላይ...', en: 'Sending...' },

  // Guest step 2/3: 6-digit code, POST /guest/otp/verify.
  'portal.guestVerify.title': { am: 'ኮዱን ያስገቡ', en: 'Enter the code' },
  'portal.guestVerify.subtitle': {
    am: 'የ6-አሃዝ ኮድ ወደ {email} ተልኳል።',
    en: 'A 6-digit code was sent to {email}.',
  },
  'portal.guestVerify.codeLabel': { am: 'የማረጋገጫ ኮድ', en: 'Verification code' },
  'portal.guestVerify.submit': { am: 'አረጋግጥ', en: 'Verify' },
  'portal.guestVerify.submitting': { am: 'በማረጋገጥ ላይ...', en: 'Verifying...' },
  'portal.guestVerify.resend': { am: 'ኮድ እንደገና ላክ', en: 'Resend code' },
  'portal.guestVerify.resending': { am: 'እንደገና በመላክ ላይ...', en: 'Resending...' },
  'portal.guestVerify.resent': { am: 'አዲስ ኮድ ተልኳል።', en: 'A new code was sent.' },
  'portal.guestVerify.changeEmail': { am: 'ኢሜይል ቀይር', en: 'Change email' },

  // Action-feedback toasts (useActionFeedback) — shared by the guest and
  // registration OTP flows.
  'portal.guest.otpSentToast': {
    am: 'የማረጋገጫ ኮድ ወደ {email} ተልኳል',
    en: 'Verification code sent to {email}',
  },
  'portal.guest.otpResentToast': { am: 'አዲስ ኮድ ተልኳል', en: 'A new code was sent' },
  'portal.guest.otpVerifiedToast': { am: 'ኢሜይል ተረጋግጧል', en: 'Email verified' },
  'portal.register.doneToast': { am: 'መለያዎ ተፈጥሯል', en: 'Account created' },

  // Guest step 3/3: organization + contact details (only reachable after
  // verify succeeds).
  'portal.guestDetails.title': { am: 'የድርጅት መረጃ', en: 'Organization details' },
  'portal.guestDetails.subtitle': {
    am: 'ስለ ድርጅትዎ እና ስለ እርስዎ ጥቂት መረጃ።',
    en: 'A few details about your organization and you.',
  },
  'portal.guestDetails.emailLabel': { am: 'ኢሜይል (የተረጋገጠ)', en: 'Email (verified)' },
  'portal.guestDetails.organizationNameOptional': {
    am: 'የድርጅት ስም (አማራጭ)',
    en: 'Organization name (optional)',
  },
  'portal.guestDetails.tin': { am: 'የግብር ከፋይ መለያ ቁጥር (አማራጭ)', en: 'TIN (optional)' },
  'portal.guestDetails.contactName': { am: 'የተጠሪ ስም', en: 'Contact name' },
  'portal.guestDetails.contactPhone': { am: 'ስልክ ቁጥር (አማራጭ)', en: 'Phone number (optional)' },
  'portal.guestDetails.address': { am: 'አድራሻ (አማራጭ)', en: 'Address (optional)' },
  'portal.guestDetails.continue': { am: 'ቀጥል', en: 'Continue' },

  // --- Customer registration (components/GuestEmailStep.jsx/
  // GuestVerifyStep.jsx reused via idPrefix, components/
  // RegisterDetailsStep.jsx, pages/RegisterPage.jsx) --------------------
  'portal.registerEmail.title': { am: 'ኢሜይልዎን ያረጋግጡ', en: 'Verify your email' },
  'portal.registerEmail.subtitle': {
    am: 'መለያ ለመክፈት የማረጋገጫ ኮድ ወደ ኢሜይልዎ እንልካለን።',
    en: "To create an account, we'll send a verification code to your email.",
  },
  'portal.registerEmail.label': { am: 'የኢሜይል አድራሻ', en: 'Email address' },
  'portal.registerEmail.submit': { am: 'ኮድ ላክ', en: 'Send code' },
  'portal.registerEmail.submitting': { am: 'በመላክ ላይ...', en: 'Sending...' },

  'portal.registerVerify.title': { am: 'ኮዱን ያስገቡ', en: 'Enter the code' },
  'portal.registerVerify.subtitle': {
    am: 'የ6-አሃዝ ኮድ ወደ {email} ተልኳል።',
    en: 'A 6-digit code was sent to {email}.',
  },
  'portal.registerVerify.codeLabel': { am: 'የማረጋገጫ ኮድ', en: 'Verification code' },
  'portal.registerVerify.submit': { am: 'አረጋግጥ', en: 'Verify' },
  'portal.registerVerify.submitting': { am: 'በማረጋገጥ ላይ...', en: 'Verifying...' },
  'portal.registerVerify.resend': { am: 'ኮድ እንደገና ላክ', en: 'Resend code' },
  'portal.registerVerify.resending': { am: 'እንደገና በመላክ ላይ...', en: 'Resending...' },
  'portal.registerVerify.resent': { am: 'አዲስ ኮድ ተልኳል።', en: 'A new code was sent.' },
  'portal.registerVerify.changeEmail': { am: 'ኢሜይል ቀይር', en: 'Change email' },

  // --- Tracking-link OTP gate (components/GuestEmailStep.jsx/
  // GuestVerifyStep.jsx reused via idPrefix, components/TrackingGate.jsx) —
  // the holder of a /proforma/:token link must prove they control the
  // inbox the proforma was sent to before any price renders. Subtitle copy
  // is deliberately framed around protecting THIS proforma's prices, not
  // generic account verification — a buyer landing here didn't ask to
  // register anything, they clicked a link they already had.
  'portal.trackingGateEmail.title': { am: 'ኢሜይልዎን ያረጋግጡ', en: 'Verify your email' },
  'portal.trackingGateEmail.subtitle': {
    am: 'የዚህ ፕሮፎርማ ዋጋዎች እንዳይታወቁ ለመጠበቅ፣ ይህ ፕሮፎርማ የተላከበትን ኢሜይል ያረጋግጡ።',
    en: "To protect this proforma's prices, verify the email it was sent to.",
  },
  'portal.trackingGateEmail.label': { am: 'የኢሜይል አድራሻ', en: 'Email address' },
  'portal.trackingGateEmail.submit': { am: 'ኮድ ላክ', en: 'Send code' },
  'portal.trackingGateEmail.submitting': { am: 'በመላክ ላይ...', en: 'Sending...' },

  'portal.trackingGateVerify.title': { am: 'ኮዱን ያስገቡ', en: 'Enter the code' },
  'portal.trackingGateVerify.subtitle': {
    am: 'የ6-አሃዝ ኮድ ወደ {email} ተልኳል።',
    en: 'A 6-digit code was sent to {email}.',
  },
  'portal.trackingGateVerify.codeLabel': { am: 'የማረጋገጫ ኮድ', en: 'Verification code' },
  'portal.trackingGateVerify.submit': { am: 'አረጋግጥ', en: 'Verify' },
  'portal.trackingGateVerify.submitting': { am: 'በማረጋገጥ ላይ...', en: 'Verifying...' },
  'portal.trackingGateVerify.resend': { am: 'ኮድ እንደገና ላክ', en: 'Resend code' },
  'portal.trackingGateVerify.resending': { am: 'እንደገና በመላክ ላይ...', en: 'Resending...' },
  'portal.trackingGateVerify.resent': { am: 'አዲስ ኮድ ተልኳል።', en: 'A new code was sent.' },
  'portal.trackingGateVerify.changeEmail': { am: 'ኢሜይል ቀይር', en: 'Change email' },

  'portal.registerDetails.title': { am: 'መለያ ይፍጠሩ', en: 'Create your account' },
  'portal.registerDetails.subtitle': {
    am: 'ስለ ድርጅትዎ መረጃ እና የይለፍ ቃል ያዘጋጁ።',
    en: 'A few details about your organization, and a password.',
  },
  'portal.registerDetails.emailLabel': { am: 'ኢሜይል (የተረጋገጠ)', en: 'Email (verified)' },
  'portal.registerDetails.organizationNameOptional': {
    am: 'የድርጅት ስም (አማራጭ)',
    en: 'Organization name (optional)',
  },
  'portal.registerDetails.tin': { am: 'የግብር ከፋይ መለያ ቁጥር (አማራጭ)', en: 'TIN (optional)' },
  'portal.registerDetails.contactName': { am: 'የተጠሪ ስም', en: 'Contact name' },
  'portal.registerDetails.contactPhone': { am: 'ስልክ ቁጥር (አማራጭ)', en: 'Phone number (optional)' },
  'portal.registerDetails.address': { am: 'አድራሻ (አማራጭ)', en: 'Address (optional)' },
  'portal.registerDetails.password': { am: 'የይለፍ ቃል', en: 'Password' },
  'portal.registerDetails.confirmPassword': { am: 'የይለፍ ቃል አረጋግጥ', en: 'Confirm password' },
  'portal.registerDetails.passwordTooShort': {
    am: 'የይለፍ ቃል ቢያንስ 8 ቁምፊዎች ሊኖረው ይገባል።',
    en: 'Password must be at least 8 characters.',
  },
  'portal.registerDetails.passwordMismatch': {
    am: 'የይለፍ ቃላቱ አይመሳሰሉም።',
    en: 'Passwords do not match.',
  },
  'portal.registerDetails.continue': { am: 'መለያ ፍጠር', en: 'Create account' },

  // --- Password reset (pages/ForgotPasswordPage.jsx, pages/
  // ResetPasswordPage.jsx) -----------------------------------------------
  'portal.forgotPassword.title': { am: 'የይለፍ ቃል ዳግም ያስጀምሩ', en: 'Reset your password' },
  'portal.forgotPassword.subtitle': {
    am: 'የመለያዎን ኢሜይል ያስገቡ፤ የዳግም ማስጀመሪያ አገናኝ እንልክልዎታለን።',
    en: "Enter your account's email and we'll send you a reset link.",
  },
  'portal.forgotPassword.submit': { am: 'አገናኝ ላክ', en: 'Send reset link' },
  'portal.forgotPassword.submitting': { am: 'በመላክ ላይ...', en: 'Sending...' },
  'portal.forgotPassword.sentTitle': { am: 'ኢሜይል ተልኳል', en: 'Check your email' },
  'portal.forgotPassword.sentBody': {
    am: 'ይህ ኢሜይል ከመለያ ጋር የተያያዘ ከሆነ፣ የይለፍ ቃል ዳግም ማስጀመሪያ አገናኝ ደርሶታል።',
    en: 'If that email is linked to an account, a password reset link is on its way.',
  },
  'portal.forgotPassword.backToLogin': { am: '← ወደ መግቢያ ተመለስ', en: '← Back to sign in' },
  'portal.forgotPassword.sentToast': { am: 'ኢሜይል ተልኳል', en: 'Reset email sent' },

  'portal.resetPassword.title': { am: 'አዲስ የይለፍ ቃል ያዘጋጁ', en: 'Set a new password' },
  'portal.resetPassword.subtitle': {
    am: 'ለመለያዎ አዲስ የይለፍ ቃል ያስገቡ።',
    en: 'Enter a new password for your account.',
  },
  'portal.resetPassword.newPassword': { am: 'አዲስ የይለፍ ቃል', en: 'New password' },
  'portal.resetPassword.confirmPassword': { am: 'የይለፍ ቃል አረጋግጥ', en: 'Confirm password' },
  'portal.resetPassword.submit': { am: 'የይለፍ ቃል ቀይር', en: 'Change password' },
  'portal.resetPassword.submitting': { am: 'በመቀየር ላይ...', en: 'Changing...' },
  'portal.resetPassword.doneTitle': { am: 'የይለፍ ቃል ተቀይሯል', en: 'Password changed' },
  'portal.resetPassword.doneBody': {
    am: 'የይለፍ ቃልዎ ተቀይሯል። አሁን በአዲሱ የይለፍ ቃል መግባት ይችላሉ።',
    en: 'Your password has been changed. You can now sign in with it.',
  },
  'portal.resetPassword.doneToast': { am: 'የይለፍ ቃል ተቀይሯል', en: 'Password changed' },
  'portal.resetPassword.goToLogin': { am: 'ወደ መግቢያ ሂድ', en: 'Go to sign in' },

  // --- Customer request list + detail: MOVED to /track (pages/
  // TrackRequestPage.jsx, pages/TrackRequestDetailPage.jsx). /account is
  // now settings only (pages/AccountSettingsPage.jsx).
  'portal.account.empty': {
    am: 'እስካሁን ምንም ጥያቄ አላቀረቡም።',
    en: "You haven't submitted any requests yet.",
  },
  'portal.account.errorBody': { am: 'ጥያቄዎችን መጫን አልተቻለም።', en: 'Could not load your requests.' },
  // The account-bar link on the request form -> the customer's request list
  // (which now lives at /track).
  'portal.account.myRequestsLink': { am: 'የእኔ ጥያቄዎች', en: 'My requests' },

  'portal.accountDetail.errorBody': { am: 'ጥያቄውን መጫን አልተቻለም።', en: 'Could not load this request.' },
  'portal.accountDetail.neededBy': {
    am: 'የሚፈለግ የማድረሻ ቀን፡ {date}',
    en: 'Required delivery date: {date}',
  },
  'portal.accountDetail.timelineTitle': { am: 'ሂደት', en: 'Activity' },
  'portal.accountDetail.timelineEmpty': { am: 'እስካሁን ምንም እንቅስቃሴ የለም።', en: 'No activity yet.' },
  'portal.accountDetail.quotationTitle': { am: 'የዋጋ ማቅረቢያ', en: 'Quotation' },
  'portal.accountDetail.quotationEmpty': {
    am: 'ይህ ጥያቄ እስካሁን ዋጋ አልተሰጠውም።',
    en: 'This request has not been priced yet.',
  },
  'portal.accountDetail.total': { am: 'ጠቅላላ ድምር', en: 'Total' },
  'portal.accountDetail.validUntil': { am: 'የሚያበቃበት ቀን', en: 'Valid until' },
  'portal.accountDetail.downloadProforma': { am: 'ፕሮፎርማ አውርድ', en: 'Download proforma' },
  'portal.accountDetail.paymentTitle': { am: 'የክፍያ ሁኔታ', en: 'Payment' },
  'portal.accountDetail.amountDue': { am: 'የሚከፈል መጠን', en: 'Amount due' },
  'portal.accountDetail.paymentStatus': { am: 'ሁኔታ', en: 'Status' },
  'portal.accountDetail.paymentDeadline': { am: 'የመጨረሻ ቀን', en: 'Deadline' },
  'portal.accountDetail.downloadPayment': { am: 'የክፍያ ሰነድ አውርድ', en: 'Download payment request' },
  'portal.accountDetail.openProformaReview': {
    am: 'ይህን ፕሮፎርማ ይገምግሙ ወይም ይቀበሉ',
    en: 'Review or accept this proforma',
  },
  'portal.accountDetail.openProforma': {
    am: 'የፕሮፎርማ ገጽ ክፈት እና ማስታወሻ ጨምር',
    en: 'Open the proforma page & notes',
  },

  'portal.accountSettings.errorBody': {
    am: 'የድርጅት መረጃን መጫን አልተቻለም።',
    en: 'Could not load organization details.',
  },
  'portal.accountSettings.title': { am: 'የድርጅት ቅንብሮች', en: 'Company settings' },
  'portal.accountSettings.companySection': { am: 'የድርጅት እና የተጠሪ መረጃ', en: 'Company & contact' },
  'portal.accountSettings.name': { am: 'የድርጅት ስም', en: 'Company name' },
  'portal.accountSettings.tin': { am: 'የግብር ከፋይ መለያ ቁጥር', en: 'TIN' },
  'portal.accountSettings.contactName': { am: 'የተጠሪ ስም', en: 'Contact name' },
  'portal.accountSettings.contactEmail': { am: 'የተጠሪ ኢሜይል', en: 'Contact email' },
  'portal.accountSettings.contactPhone': { am: 'ስልክ ቁጥር', en: 'Phone number' },
  'portal.accountSettings.address': { am: 'አድራሻ', en: 'Address' },
  'portal.accountSettings.notificationsSection': {
    am: 'የማሳወቂያ ምርጫዎች',
    en: 'Notification preferences',
  },
  'portal.accountSettings.notificationsHint': {
    am: 'ስለ ጥያቄዎችዎ በየትኞቹ መንገዶች ማሳወቂያ ማግኘት እንደሚፈልጉ ይምረጡ።',
    en: 'Choose which channels notify you about your requests.',
  },
  'portal.accountSettings.notifyEmail': { am: 'በኢሜይል አሳውቀኝ', en: 'Notify me by email' },
  'portal.accountSettings.notifyTelegram': { am: 'በቴሌግራም አሳውቀኝ', en: 'Notify me by Telegram' },
  'portal.accountSettings.notifyWhatsapp': { am: 'በዋትስአፕ አሳውቀኝ', en: 'Notify me by WhatsApp' },
  'portal.accountSettings.savedFlash': { am: 'ተቀምጧል', en: 'Saved' },
  'portal.accountSettings.savedToast': {
    am: 'የድርጅት መረጃ ተቀምጧል',
    en: 'Company details saved',
  },
  'portal.accountSettings.saving': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },
  'portal.accountSettings.saveButton': { am: 'ለውጦችን አስቀምጥ', en: 'Save changes' },

  // Delete account (bottom of this page, destructive-action styling —
  // matches admin/src/components/TwoFactorSettings.module.css's
  // .dangerButton visual weight). The organization record, its requests,
  // and its issued proformas are kept (commercial/tax records); only this
  // login's name/email/phone are removed. Static policy copy — not derived
  // from the API response.
  'portal.accountSettings.dangerSection': { am: 'መለያ ሰርዝ', en: 'Delete account' },
  'portal.accountSettings.dangerIntro': {
    am: 'የመግቢያ መለያዎን መሰረዝ ይችላሉ። ይህ ድርጊት መቀልበስ አይቻልም።',
    en: 'You can delete your login. This action cannot be undone.',
  },
  'portal.accountSettings.dangerKept': {
    am: 'የሚቀሩት፦ የድርጅትዎ መዝገብ፣ ያስገቧቸው ጥያቄዎች፣ እና የተሰጡ ፕሮፎርማዎች — እነዚህ የንግድ እና የግብር ማስረጃ ናቸው።',
    en: 'What stays: your organization record, its requests, and its issued proformas — these are commercial and tax records.',
  },
  'portal.accountSettings.dangerRemoved': {
    am: 'የሚወገዱት፦ በዚህ መግቢያ ላይ ያለው ስምዎ፣ ኢሜይልዎ፣ እና ስልክ ቁጥርዎ ብቻ።',
    en: 'What is removed: only your name, email, and phone number on this login.',
  },
  'portal.accountSettings.dangerConfirmLabel': {
    am: 'ለማረጋገጥ "DELETE MY ACCOUNT" ብለው ይተይቡ (በእንግሊዝኛ፣ በትክክል እንደተጻፈው)',
    en: 'To confirm, type "DELETE MY ACCOUNT" exactly as shown',
  },
  'portal.accountSettings.dangerButton': { am: 'መለያዬን ሰርዝ', en: 'Delete my account' },
  'portal.accountSettings.dangerDeletedToast': {
    am: 'መለያዎ ተሰርዟል፦ ስምዎ፣ ኢሜይልዎ እና ስልክ ቁጥርዎ ተወግደዋል። የድርጅትዎ መዝገብ እና ያለፉ ጥያቄዎችዎ/ፕሮፎርማዎችዎ ተጠብቀዋል።',
    en: 'Your account has been deleted: your name, email, and phone number have been removed. Your organization record and past requests/proformas are kept.',
  },

  // buyerStatus — MUST match services/trackingService.js's BUYER_STATUSES
  // exactly (the buyer-facing status vocabulary the server computes; this
  // app never reads raw internal request/quotation status).
  'portal.buyerStatus.received': { am: 'ደርሷል', en: 'Received' },
  'portal.buyerStatus.being_priced': { am: 'ዋጋ በመሰጠት ላይ', en: 'Being priced' },
  'portal.buyerStatus.proforma_ready': { am: 'ፕሮፎርማ ዝግጁ ነው', en: 'Proforma ready' },
  'portal.buyerStatus.accepted': { am: 'ተቀባይነት አግኝቷል', en: 'Accepted' },
  'portal.buyerStatus.awaiting_payment': { am: 'ክፍያ በመጠበቅ ላይ', en: 'Awaiting payment' },
  'portal.buyerStatus.paid': { am: 'ተከፍሏል', en: 'Paid' },
  'portal.buyerStatus.declined': { am: 'ውድቅ ተደርጓል', en: 'Declined' },
  'portal.buyerStatus.expired': { am: 'ጊዜው አልፎበታል', en: 'Expired' },
  'portal.buyerStatus.revised': { am: 'ተሻሽሏል', en: 'Revised' },
  'portal.buyerStatus.cancelled': { am: 'ተሰርዟል', en: 'Cancelled' },

  // timelineEvent — MUST match trackingService.js#buildTimeline's own entry
  // keys exactly.
  'portal.timelineEvent.received': { am: 'ጥያቄ ደርሷል', en: 'Request received' },
  'portal.timelineEvent.revised': { am: 'ዋጋ ተሻሽሏል', en: 'Quote revised' },
  'portal.timelineEvent.proforma_ready': { am: 'ፕሮፎርማ ተልኳል', en: 'Proforma sent' },
  'portal.timelineEvent.accepted': { am: 'ተቀብለዋል', en: 'You accepted' },
  'portal.timelineEvent.declined': { am: 'ውድቅ አድርገዋል', en: 'You declined' },
  'portal.timelineEvent.awaiting_payment': { am: 'ክፍያ ጥያቄ ተልኳል', en: 'Payment requested' },
  'portal.timelineEvent.paid': { am: 'ክፍያ ተረጋግጧል', en: 'Payment confirmed' },
  'portal.timelineEvent.cancelled': { am: 'ጥያቄው ተሰርዟል', en: 'Request cancelled' },

  // paymentStatus — MUST match payment_requests.status exactly (migration
  // 010: ENUM('awaiting_payment','paid')).
  'portal.paymentStatus.awaiting_payment': { am: 'ክፍያ በመጠበቅ ላይ', en: 'Awaiting payment' },
  'portal.paymentStatus.paid': { am: 'ተከፍሏል', en: 'Paid' },

  // --- In-page proforma (pages/ProformaPage.jsx, reached at
  // /proforma/:token). An HTML rendering of the same figures as the signed
  // PDF, readable on a phone, plus the per-line review and the notes log.
  'portal.proforma.errorBody': {
    am: 'ይህን ፕሮፎርማ መጫን አልተቻለም። አገናኙ ትክክል ላይሆን ወይም ጊዜው አልፎበት ሊሆን ይችላል።',
    en: 'Could not load this proforma. The link may be invalid or expired.',
  },
  'portal.proforma.title': { am: 'ፕሮፎርማ', en: 'Proforma' },
  'portal.proforma.validUntil': { am: 'የሚያበቃበት ቀን፡ {date}', en: 'Valid until {date}' },
  'portal.proforma.downloadPdf': { am: 'የተፈረመ PDF አውርድ', en: 'Download signed PDF' },
  'portal.proforma.notReadyYet': {
    am: 'ለዚህ ጥያቄ ገና ፕሮፎርማ አልተዘጋጀም። ዝግጁ ሲሆን ማሳወቂያ ይደርስዎታል።',
    en: 'No proforma has been prepared for this request yet. You will be notified when it is ready.',
  },
  'portal.proforma.paymentTitle': { am: 'ክፍያ', en: 'Payment' },
  'portal.proforma.expiredTitle': { am: 'ይህ ፕሮፎርማ ጊዜው አልፎበታል', en: 'This proforma has expired' },
  'portal.proforma.expiredBody': {
    am: 'የዋጋ ማቅረቢያው ጊዜ አልፎበታል። አዲስ ዋጋ እንዲሰጥዎ የሽያጭ ቡድናችንን መጠየቅ ይችላሉ።',
    en: 'The quoted prices are past their validity date. You can ask our sales team to re-price this request.',
  },
  'portal.proforma.requestUpdatedPrices': { am: 'አዲስ ዋጋ ጠይቅ', en: 'Request updated prices' },
  'portal.proforma.requoteRequested': {
    am: 'ጥያቄዎ ተልኳል። የሽያጭ ቡድናችን አዲስ ዋጋ ያዘጋጃል።',
    en: 'Request sent. Our sales team will prepare updated prices.',
  },

  // Cancel a request (tracking-token action, mirrors accept/decline/requote
  // above) — shown only while buyerStatus is 'received' or 'being_priced',
  // i.e. before a proforma exists. Once a proforma exists, decline is the
  // right action instead.
  'portal.proforma.cancelTitle': { am: 'ይህን ጥያቄ ሰርዝ', en: 'Cancel this request' },
  'portal.proforma.cancelHint': {
    am: 'ይህ ጥያቄ እንደገና ላይመለስ ይሰረዛል። ለምን እንደሚሰርዙ ምክንያት ያስፍሩ።',
    en: 'This request will be cancelled and cannot be undone. Tell us why you are cancelling.',
  },
  'portal.proforma.cancelReasonLabel': { am: 'የመሰረዣ ምክንያት', en: 'Reason for cancelling' },
  'portal.proforma.cancelButton': { am: 'ጥያቄ ሰርዝ', en: 'Cancel request' },
  'portal.proforma.cancelConfirmTitle': { am: 'ጥያቄ ይሰረዝ?', en: 'Cancel this request?' },
  'portal.proforma.cancelConfirmBody': {
    am: 'ይህን ማድረግ መቀልበስ አይቻልም። ጥያቄው ይዘጋል እና ተጨማሪ ዋጋ አይሰጠውም።',
    en: 'This cannot be undone. The request will be closed and no further pricing will happen on it.',
  },
  'portal.proforma.cancelledToast': { am: 'ጥያቄው ተሰርዟል።', en: 'The request has been cancelled.' },
  'portal.proforma.cancelledTitle': { am: 'ይህ ጥያቄ ተሰርዟል', en: 'This request was cancelled' },
  'portal.proforma.cancelledBody': {
    am: 'ይህን ጥያቄ ሰርዘውታል፦ {reason}',
    en: 'You cancelled this request: {reason}',
  },
  'portal.proforma.cancelledBodyNoReason': {
    am: 'ይህ ጥያቄ ተሰርዟል።',
    en: 'This request has been cancelled.',
  },
  'error.EMPTY_CANCEL_REASON': {
    am: 'የመሰረዣ ምክንያት ባዶ መሆን አይችልም።',
    en: 'A reason for cancelling is required.',
  },
  'error.REQUEST_NOT_CANCELLABLE': {
    am: 'ይህ ጥያቄ አሁን ሊሰረዝ አይችልም። ፕሮፎርማ ካለ በምትኩ ውድቅ ያድርጉት።',
    en: 'This request cannot be cancelled right now. If a proforma already exists, decline it instead.',
  },
  'error.INVALID_CONFIRMATION': {
    am: 'የተየቡት ጽሑፍ "DELETE MY ACCOUNT" ከሚለው ጋር በትክክል አይመሳሰልም።',
    en: 'What you typed does not exactly match "DELETE MY ACCOUNT".',
  },
  'error.ACCOUNT_ALREADY_DELETED': {
    am: 'ይህ መለያ አስቀድሞ ተሰርዟል።',
    en: 'This account has already been deleted.',
  },

  'portal.proforma.col.line': { am: 'ተራ', en: 'Line' },
  'portal.proforma.col.description': { am: 'መግለጫ', en: 'Description' },
  'portal.proforma.col.qty': { am: 'ብዛት', en: 'Qty' },
  'portal.proforma.col.unitPrice': { am: 'የአንዱ ዋጋ', en: 'Unit price' },
  'portal.proforma.col.lineTotal': { am: 'ድምር', en: 'Line total' },
  'portal.proforma.subtotal': { am: 'ንዑስ ድምር', en: 'Subtotal' },
  'portal.proforma.vat': { am: 'ተ.እ.ታ', en: 'VAT' },
  'portal.proforma.total': { am: 'ጠቅላላ ድምር', en: 'Total' },

  // --- Proforma review (components/ProformaReview.jsx). A per-line
  // decision — accept / change quantity / decline with a reason — submitted
  // as one whole decision. Categories MUST match routes/trackingRoutes.js's
  // reviewBodySchema decline enum exactly.
  'portal.review.title': { am: 'ይህን ፕሮፎርማ ይገምግሙ', en: 'Review this proforma' },
  'portal.review.intro': {
    am: 'ለእያንዳንዱ መስመር ውሳኔ ይስጡ። ውሳኔዎ በአንድ ጊዜ ይላካል።',
    en: 'Choose a decision for each line. Your whole decision is submitted at once.',
  },
  'portal.review.qtyAt': {
    am: '{qty} {unit} በ {price}',
    en: '{qty} {unit} at {price}',
  },
  'portal.review.accept': { am: 'ተቀበል', en: 'Accept' },
  'portal.review.changeQty': { am: 'ብዛት ቀይር', en: 'Change quantity' },
  'portal.review.decline': { am: 'አትቀበል', en: 'Decline' },
  'portal.review.newQtyLabel': { am: 'አዲስ ብዛት', en: 'New quantity' },
  'portal.review.reasonLabel': { am: 'ምክንያት', en: 'Reason' },
  'portal.review.noteLabel': { am: 'ተጨማሪ ማብራሪያ', en: 'More detail' },
  'portal.review.currentTotal': { am: 'የአሁኑ ጠቅላላ ድምር፡ {total}', en: 'Current total: {total}' },
  'portal.review.recalcNote': {
    am: 'ማሳሰቢያ፡ የተቀየረው ወይም ውድቅ የተደረገው መስመር ዋጋ ከጸደቀ በኋላ በአዲስ ማሻሻያ ላይ እንደገና ይሰላል።',
    en: 'Changed or declined lines will be re-totalled on a new revision after approval.',
  },
  'portal.review.submit': { am: 'ውሳኔ ላክ', en: 'Submit decision' },
  'portal.review.confirm.title': { am: 'ውሳኔዎን ያረጋግጡ', en: 'Confirm your decision' },
  'portal.review.confirm.acceptAll': {
    am: 'ሁሉንም መስመሮች እንደተጠቀሱት ይቀበላሉ። ይህ ፕሮፎርማውን ተቀባይነት ያሰጠዋል እና የክፍያ ጥያቄ ይፈጥራል።',
    en: 'You are accepting every line as quoted. This accepts the proforma and creates a payment request.',
  },
  'portal.review.confirm.partial': {
    am: '{accepted} ተቀባይነት፣ {changed} ብዛት ተቀይሯል፣ {declined} ውድቅ ተደርጓል። አሁን ያለው ፕሮፎርማ ይተካና የተቀበሏቸው መስመሮች ላይ አዲስ ማሻሻያ ለማጽደቅ ይላካል።',
    en: '{accepted} accepted, {changed} quantity changes, {declined} declined. The current proforma is superseded and a new revision of the lines you accepted goes for approval.',
  },
  'portal.review.confirm.submit': { am: 'አዎ፣ ላክ', en: 'Yes, submit' },
  'portal.review.error.noteRequired': {
    am: '"ሌላ" ሲመርጡ ማብራሪያ ያስፈልጋል።',
    en: 'A note is required when the reason is "Other".',
  },
  'portal.review.error.badQty': {
    am: 'ለተቀየረ መስመር ትክክለኛ ብዛት ያስገቡ።',
    en: 'Enter a valid quantity for a changed line.',
  },
  'portal.review.outcome.accepted': {
    am: 'ፕሮፎርማው ተቀባይነት አግኝቷል። የክፍያ ዝርዝሮች እዚህ ገጽ ላይ ይታያሉ።',
    en: 'Proforma accepted. Payment details will appear on this page.',
  },
  'portal.review.outcome.declined': {
    am: 'ውሳኔዎ ተመዝግቧል። ሁሉንም መስመሮች ውድቅ አድርገዋል።',
    en: 'Your decision is recorded. You declined every line.',
  },
  'portal.review.outcome.revised': {
    am: 'ውሳኔዎ ተመዝግቧል። የተቀበሏቸው መስመሮች ላይ አዲስ ማሻሻያ እየተዘጋጀ ነው።',
    en: 'Your decision is recorded. A revised proforma of the lines you accepted is being prepared.',
  },

  // declineReason — MUST match routes/trackingRoutes.js reviewBodySchema's
  // declineCategory enum (migration 016 added 'budget_cancelled').
  'portal.declineReason.price': { am: 'ዋጋ በጣም ከፍ ብሏል', en: 'Price too high' },
  'portal.declineReason.delivery_time': { am: 'የማድረሻ ጊዜ አይመችም', en: 'Delivery time' },
  'portal.declineReason.specification_mismatch': {
    am: 'ዝርዝሩ ከፍላጎቴ ጋር አይመሳሰልም',
    en: 'Specification mismatch',
  },
  'portal.declineReason.chose_another_supplier': {
    am: 'ሌላ አቅራቢ መርጫለሁ',
    en: 'Chose another supplier',
  },
  'portal.declineReason.budget_cancelled': { am: 'በጀቱ ተሰርዟል', en: 'Budget cancelled' },
  'portal.declineReason.other': { am: 'ሌላ', en: 'Other' },

  // --- Notes log (components/NotesLog.jsx). One shared append-only log per
  // request, buyer + staff, no edit/delete.
  'portal.notes.title': { am: 'ማስታወሻዎች', en: 'Notes' },
  'portal.notes.empty': {
    am: 'እስካሁን ምንም ማስታወሻ የለም። ጥያቄ ካለዎት እዚህ ይጻፉ።',
    en: 'No notes yet. Ask a question here if you have one.',
  },
  'portal.notes.fromStaff': { am: 'የሽያጭ ቡድን', en: 'Sales team' },
  'portal.notes.fromYou': { am: 'እርስዎ', en: 'You' },
  'portal.notes.addLabel': { am: 'ማስታወሻ ጨምር', en: 'Add a note' },
  'portal.notes.placeholder': {
    am: 'ስለዚህ ጥያቄ የሆነ ነገር ይጻፉ...',
    en: 'Write something about this request...',
  },
  'portal.notes.send': { am: 'ላክ', en: 'Send' },
  'portal.notes.addedToast': { am: 'ማስታወሻዎ ተጨምሯል', en: 'Your note was added' },

  // Request form — shared by the guest path (after details) and the
  // account-holder path (portal/src/pages/RequestFormPage.jsx).
  'portal.requestForm.title': { am: 'ጥያቄዎን ያቅርቡ', en: 'Submit your request' },
  'portal.requestForm.signedInAs': { am: 'እንደ {name} ገብተዋል', en: 'Signed in as {name}' },
  'portal.requestForm.neededBy': {
    am: 'የሚፈለግ የማድረሻ ቀን (አማራጭ)',
    en: 'Required delivery date (optional)',
  },
  'portal.requestForm.buyerNote': { am: 'ማስታወሻ (አማራጭ)', en: 'Note (optional)' },
  'portal.requestForm.buyerNotePlaceholder': {
    am: 'ለሽያጭ ቡድናችን ተጨማሪ መረጃ...',
    en: 'Any extra detail for our sales team...',
  },
  'portal.requestForm.purpose': { am: 'ዓላማ (አማራጭ)', en: 'Purpose (optional)' },
  'portal.requestForm.purposePlaceholder': {
    am: 'ለምሳሌ፡ የአይሲቲ ዳይሬክቶሬት፣ የቅርንጫፍ ጽ/ቤት እድሳት፣ ጨረታ AAU/2026/1183',
    en: 'e.g. ICT Directorate, branch office refresh, tender AAU/2026/1183',
  },
  // Upload drop zone (entry 45 — merges the Excel/BOQ-upload and PDF-attach
  // entry paths into this one form, above the always-available manual
  // lines). Accepts .xlsx/.xls/.csv/.pdf — see backend/src/utils/
  // requestUploadMulter.js for the matching server-side list.
  'portal.requestForm.upload.title': { am: 'ፋይል ያያይዙ (አማራጭ)', en: 'Attach a file (optional)' },
  'portal.requestForm.upload.dropHint': {
    am: 'ፋይል እዚህ ይጣሉ፣ ወይም',
    en: 'Drag a file here, or',
  },
  'portal.requestForm.upload.chooseFile': { am: 'ፋይል ምረጥ', en: 'Choose file' },
  'portal.requestForm.upload.acceptedTypes': {
    am: '.xlsx, .xls, .csv, ወይም .pdf',
    en: '.xlsx, .xls, .csv, or .pdf',
  },
  'portal.requestForm.upload.remove': { am: 'ፋይል አስወግድ', en: 'Remove file' },
  'portal.requestForm.upload.invalidType': {
    am: 'የ .xlsx, .xls, .csv, ወይም .pdf ፋይሎች ብቻ ተቀባይነት አላቸው።',
    en: 'Only .xlsx, .xls, .csv, or .pdf files are accepted.',
  },
  // Shown once a file is attached — sets expectations for BOTH parse
  // behaviors (an .xlsx may later be read automatically; anything else,
  // including every PDF, is read by a person) without promising something
  // this build doesn't do (see requestUploadService.js's own comment on
  // why only .xlsx is auto-parsed today).
  'portal.requestForm.upload.hint': {
    am: 'ፋይልዎን የሽያጭ ቡድናችን ይገመግማል። ከታች ተጨማሪ ዕቃዎችን መጨመርም ይችላሉ።',
    en: 'Our sales team will review your file. You can also add items below.',
  },
  'portal.requestForm.linesTitle': { am: 'የሚያስፈልጉ ዕቃዎች', en: 'Items needed' },
  'portal.requestForm.lineDescription': { am: 'ምርት/መግለጫ', en: 'Product / description' },
  'portal.requestForm.lineDescriptionPlaceholder': {
    am: 'ለምሳሌ፦ A4 የህትመት ወረቀት',
    en: 'e.g. A4 printing paper',
  },
  'portal.requestForm.lineUnit': { am: 'መለኪያ (አማራጭ)', en: 'Unit (optional)' },
  // Now the "Other" text input's placeholder only (unit-dropdown task) —
  // the 10 examples this string used to show are themselves dropdown
  // options below, so this input is only ever reached for a unit NOT in
  // that list.
  'portal.requestForm.lineUnitPlaceholder': {
    am: 'ለምሳሌ፦ ደርዘን',
    en: 'e.g. dozen, drum',
  },
  // Unit dropdown options (unit-dropdown task) — the displayed label's `en`
  // value is deliberately the exact canonical token submitted as `rawUnit`
  // (see RequestLinesForm.jsx's UNIT_OPTIONS) for the 10 listed units; only
  // the `am` side is a translation. "Other" is the escape hatch that reveals
  // the free-text input instead of submitting a listed value.
  'portal.requestForm.unitUnspecified': { am: 'አልተገለጸም', en: 'Not specified' },
  'portal.requestForm.unitPcs': { am: 'ፒስ', en: 'pcs' },
  'portal.requestForm.unitCarton': { am: 'ካርቶን', en: 'carton' },
  'portal.requestForm.unitBox': { am: 'ሳንዱክ', en: 'box' },
  'portal.requestForm.unitSet': { am: 'ስብስብ', en: 'set' },
  'portal.requestForm.unitPack': { am: 'ጥቅል', en: 'pack' },
  'portal.requestForm.unitRoll': { am: 'ጥቅልል', en: 'roll' },
  'portal.requestForm.unitMetre': { am: 'ሜትር', en: 'metre' },
  'portal.requestForm.unitKg': { am: 'ኪሎግራም', en: 'kg' },
  'portal.requestForm.unitLitre': { am: 'ሊትር', en: 'litre' },
  'portal.requestForm.unitReam': { am: 'ሪም', en: 'ream' },
  'portal.requestForm.unitOther': { am: 'ሌላ', en: 'Other' },
  'portal.requestForm.lineQty': { am: 'ብዛት', en: 'Quantity' },
  // No literal "+" in this string — RequestLinesForm.jsx already renders a
  // lucide-react <Plus> icon right before it (a real, screenshot-caught
  // double-plus bug: "+ + Add item").
  'portal.requestForm.addLine': { am: 'ዕቃ ጨምር', en: 'Add item' },
  // Catalog-picker task, Entry Point 1 — same no-literal-"+"-icon note as
  // addLine just above (RequestLinesForm.jsx renders a lucide-react
  // <Search> icon before this one).
  'portal.requestForm.addFromCatalog': { am: 'ከካታሎግ ጨምር', en: 'Add from catalog' },
  'portal.requestForm.catalogSearchPlaceholder': {
    am: 'የምርት ስም ወይም ኮድ ይፈልጉ...',
    en: 'Search by product name or SKU...',
  },
  'portal.requestForm.catalogSearchButton': { am: 'ፈልግ', en: 'Search' },
  'portal.requestForm.catalogSearching': { am: 'በመፈለግ ላይ...', en: 'Searching...' },
  'portal.requestForm.catalogSearchError': {
    am: 'ፍለጋው አልተሳካም። እንደገና ይሞክሩ።',
    en: 'Search failed. Please try again.',
  },
  'portal.requestForm.catalogNoResults': {
    am: 'ምንም ምርት አልተገኘም።',
    en: 'No products found.',
  },
  // The quiet marker on a catalog-sourced line, distinct from a typed one.
  'portal.requestForm.fromCatalog': { am: 'ከካታሎግ', en: 'From catalog' },
  'portal.requestForm.removeLine': { am: 'ይህን ዕቃ አስወግድ', en: 'Remove this item' },
  'portal.requestForm.emptyLines.title': { am: 'ገና ዕቃ አልተጨመረም', en: 'No items yet' },
  'portal.requestForm.emptyLines.body': {
    am: 'ከታች "ዕቃ ጨምር" የሚለውን ተጭነው የሚያስፈልግዎትን የመጀመሪያ ዕቃ ያክሉ።',
    en: 'Tap "Add item" below to add the first thing you need.',
  },
  // Shown instead of .body specifically when a file is already attached —
  // zero lines is a genuinely fine state there (this task's own brief: "A
  // PDF-only request has zero lines — that is a valid state, not an
  // error"), so this reassures rather than nudging the buyer to type
  // everything the file might already cover.
  'portal.requestForm.emptyLines.bodyWithFile': {
    am: 'ምንም ችግር የለውም — ከፋይልዎ እንይዛለን። የጎደለ ካለ ከታች ይጨምሩ።',
    en: "That's fine — we'll work from your file. Add anything extra below.",
  },
  'portal.requestForm.lineRequired': {
    am: 'ቢያንስ አንድ ዕቃ ያክሉ ወይም ፋይል ያያይዙ።',
    en: 'Add at least one item or attach a file.',
  },
  'portal.requestForm.descriptionRequired': {
    am: 'መግለጫ ያስፈልጋል።',
    en: 'A description is required.',
  },
  'portal.requestForm.qtyInvalid': { am: 'ትክክለኛ ብዛት ያስገቡ።', en: 'Enter a valid quantity.' },
  'portal.requestForm.linesCount': { am: '{count} ዕቃ(ዎች)', en: '{count} item(s)' },
  'portal.requestForm.submit': { am: 'ጥያቄ አስገባ', en: 'Submit request' },
  'portal.requestForm.submittedToast': {
    am: 'ጥያቄ {refNo} ገብቷል',
    en: 'Request {refNo} submitted',
  },
  'portal.requestForm.submitting': {
    am: 'ጥያቄ በማስገባት ላይ — እባክዎ ይጠብቁ...',
    en: 'Submitting your request — please wait...',
  },

  // Column mapping is no longer a buyer step — the buyer uploads and
  // submits, and a salesperson maps the columns in the pricing workspace.
  // The portal only tells the buyer the request was received.

  // Confirmation — end of this task's scope (no status tracker yet).
  'portal.confirmation.title': { am: 'ጥያቄዎ ደርሶናል', en: 'Your request has been received' },
  'portal.confirmation.refNoLabel': { am: 'የጥያቄ ቁጥር', en: 'Request number' },
  'portal.confirmation.whatNext': {
    am: 'የሽያጭ ቡድናችን ጥያቄዎን ገምግሞ ዋጋ ያወጣል፣ የተፈረመ ፕሮፎርማ በኢሜይል ይደርስዎታል።',
    en: 'Our sales team will review and price your request, then send a signed proforma to your email.',
  },
  'portal.confirmation.newRequest': { am: 'ሌላ ጥያቄ አስገባ', en: 'Submit another request' },
  // Guest-only (confirmation-screen task): only guestService.
  // submitGuestRequest's response carries trackingToken, so only
  // pages/GuestPage.jsx passes it to this component — RequestConfirmation
  // renders neither string at all for the account-holder path (RequestFormPage.jsx),
  // which already has its own, always-available /track list.
  'portal.confirmation.trackThisRequest': { am: 'ይህን ጥያቄ ተከተል', en: 'Track this request' },
  'portal.confirmation.saveRefHint': {
    am: 'የጥያቄ ቁጥርዎን እና የተጠቀሙበትን ኢሜይል ያስቀምጡ — ከላይ ባለው "ጥያቄ ተከተል" በመጠቀም በማንኛውም ጊዜ ይህን ጥያቄ መፈለግ ይችላሉ።',
    en: 'Save your request number and the email you used — you can look this request up anytime from "Track a request" using them.',
  },

  // Landing page: featured products (entry 39) — GET /public/catalog/featured,
  // genuinely public, no price ever included (see productRepository
  // PRODUCT_COLUMNS). 'portal.catalog.requestPrice' is shared with the
  // catalog browse screen's own product cards below (components/ProductCard.jsx).
  'portal.landing.featuredTitle': { am: 'ተለይተው የቀረቡ ምርቶች', en: 'Featured products' },
  // Shared by the featured-products section's subtitle AND the catalog
  // browse page's own subtitle — same "no prices" hint, one string.
  'portal.catalog.noPricesHint': {
    am: 'ዋጋ አይታይም — ይምረጡ እና የዋጋ ጥያቄ ያቅርቡ።',
    en: 'No prices shown — pick something and request a price.',
  },
  'portal.catalog.requestPrice': { am: 'ዋጋ ጠይቅ', en: 'Request price' },
  'portal.catalog.addToRequest': { am: 'ለጥያቄ ጨምር', en: 'Add to request' },
  'portal.catalog.productSku': { am: 'ኮድ: {value}', en: 'SKU: {value}' },

  // Condition labels (utils/productDisplay.js's conditionLabelId) — portal's
  // own copy of the same four ENUM values admin/CLAUDE.md's catalog screens
  // already translate (shared/i18n/admin.js), needed here too since
  // ProductCard.jsx's condition-badge fallback and RequestLinesForm.jsx's
  // catalog-search result rows (catalog-picker task) both call t() with
  // these ids directly, and admin.js's string table isn't loaded into this
  // app's bundle at all (each app composes its own, shared/i18n's whole
  // reason for existing) — this id was being looked up against nothing
  // before this entry, rendering as a visible "??catalog.condition.new??"
  // the moment that fallback path was actually hit.
  'catalog.condition.new': { am: 'አዲስ', en: 'New' },
  'catalog.condition.refurbishedManufacturer': {
    am: 'በአምራች የታደሰ',
    en: 'Refurbished (Manufacturer)',
  },
  'catalog.condition.refurbishedInhouse': { am: 'በእኛ የታደሰ', en: 'Refurbished (In-house)' },
  'catalog.condition.used': { am: 'ያገለገለ', en: 'Used' },

  // Catalog browse screen (/browse): two-column catalog — a persistent
  // category tree (sidebar on desktop, chip row on tablet, stacked on
  // mobile) beside the product grid, which shows all products on arrival and
  // filters in place. Free-text search runs within the selected category.
  'portal.catalog.title': { am: 'ካታሎግ', en: 'Browse catalog' },
  'portal.catalog.searchPlaceholder': { am: 'ምርት ፈልግ...', en: 'Search products...' },
  'portal.catalog.allProducts': { am: 'ሁሉም ምርቶች', en: 'All products' },
  'portal.catalog.categoriesHeading': { am: 'ምድቦች', en: 'Categories' },
  'portal.catalog.expandCategory': { am: 'ንዑስ ምድቦችን አሳይ', en: 'Show subcategories' },
  'portal.catalog.collapseCategory': { am: 'ንዑስ ምድቦችን ደብቅ', en: 'Hide subcategories' },
  'portal.catalog.clearSearch': { am: 'ፍለጋን አጽዳ', en: 'Clear search' },
  'portal.catalog.showingAll': {
    am: '{count} ምርቶች በሙሉ',
    en: 'Showing all {count} products',
  },
  'portal.catalog.showingInCategory': {
    am: 'በ"{category}" ውስጥ {count} ምርቶች',
    en: '{count} products in {category}',
  },
  'portal.catalog.showingSearch': {
    am: 'ለ"{term}" {count} ውጤቶች',
    en: '{count} results for “{term}”',
  },
  'portal.catalog.showingSearchInCategory': {
    am: 'ለ"{term}" በ"{category}" ውስጥ {count} ውጤቶች',
    en: '{count} results for “{term}” in {category}',
  },
  'portal.catalog.emptyCategory.title': { am: 'እዚህ ገና ምርት የለም', en: 'No products here yet' },
  'portal.catalog.emptyCategory.body': {
    am: 'ሌላ ምድብ ይሞክሩ፣ ወይም የሚፈልጉትን ገልጸው ጥያቄ ያስገቡ።',
    en: 'Try a different category, or submit a request describing what you need.',
  },
  'portal.catalog.noResults': {
    am: 'ከፍለጋዎ ጋር የሚዛመድ ምርት አልተገኘም።',
    en: 'No products match your search.',
  },
  'portal.catalog.errorBody': { am: 'ካታሎግ መጫን አልተቻለም።', en: 'Could not load the catalog.' },

  // --- Product detail page (/catalog/:id, pages/ProductDetailPage.jsx) ---
  // Reached from a featured card (components/ProductCard.jsx's
  // `variant="featured"`). No price/stock anywhere — same rule as every
  // other portal catalog screen.
  'portal.productDetail.categoryLabel': { am: 'ምድብ', en: 'Category' },
  'portal.productDetail.brandLabel': { am: 'ብራንድ', en: 'Brand' },
  'portal.productDetail.modelLabel': { am: 'ሞዴል', en: 'Model' },
  'portal.productDetail.notFoundTitle': {
    am: 'ይህ ምርት አልተገኘም',
    en: 'This product could not be found',
  },
  'portal.productDetail.notFoundBody': {
    am: 'ይህ ምርት ከእንግዲህ አይገኝም ወይም ተወግዷል።',
    en: 'This product is no longer available.',
  },
  'portal.productDetail.errorBody': {
    am: 'የምርት መረጃን መጫን አልተቻለም።',
    en: 'Could not load this product.',
  },
  'portal.productDetail.backToCatalog': { am: '← ወደ ካታሎግ ተመለስ', en: '← Back to catalog' },

  // Site header/footer (Direction B desktop redesign, entry 41) —
  // components/SiteHeader.jsx, components/SiteFooter.jsx, LandingPage.jsx
  // only for now, not every screen (root CLAUDE.md's mobile-first portal
  // rules are unchanged elsewhere). Nav's "Sign in" reuses
  // 'portal.login.title' rather than a new key.
  'portal.nav.catalog': { am: 'ካታሎግ', en: 'Catalog' },
  'portal.nav.sendRequest': { am: 'ጥያቄ ላክ', en: 'Send a request' },
  'portal.nav.trackRequest': { am: 'ጥያቄ ይከታተሉ', en: 'Track a request' },
  // Entry Point 2 (catalog-picker task): the header's pending-items count
  // badge — SiteHeader's own nav row hides below 768px, but .actions
  // (where this lives) stays visible at every width, so this is the one
  // place a mobile buyer reliably sees it.
  'portal.nav.pendingCount': { am: '{count} ዕቃዎች ተጨምረዋል', en: '{count} items added' },
  // A logged-in customer sees their own request list at /track, so the nav
  // label says so rather than "Track a request".
  'portal.nav.myRequests': { am: 'የእኔ ጥያቄዎች', en: 'My requests' },
  'portal.nav.contact': { am: 'ያግኙን', en: 'Contact' },
  'portal.nav.back': { am: '← ተመለስ', en: '← Back' },
  // Customer-accounts task: SiteHeader.jsx's "Sign in" link becomes this
  // when a customer session is active.
  'portal.nav.myAccount': { am: 'የእኔ መለያ', en: 'My account' },

  // The footer's address / hours / phone / email are read from the settings
  // table (GET /public/company), not from i18n — only this heading is a
  // fixed UI string. See components/SiteFooter.jsx.
  'portal.footer.contactTitle': { am: 'ያግኙን', en: 'Contact us' },

  // /track (pages/TrackRequestPage.jsx). A logged-in customer sees their own
  // request list; a guest gets the reference-number + email lookup
  // (POST /track/lookup). pages/TrackRequestDetailPage.jsx renders one
  // request's full view via components/RequestTrackingView.jsx.
  'portal.track.title': { am: 'ጥያቄዎን ይከታተሉ', en: 'Track your request' },
  'portal.track.guestLede': {
    am: 'የጥያቄ ቁጥርዎን እና በጥያቄው ላይ ያለውን ኢሜይል ያስገቡ። ሁኔታውን፣ ሂደቱን እና ፕሮፎርማ ካለ ማውረጃውን ያሳየዎታል።',
    en: 'Enter your reference number and the email on the request. We’ll show its status, timeline, and proforma download if one is ready.',
  },
  'portal.track.refNoLabel': { am: 'የጥያቄ ቁጥር', en: 'Reference number' },
  'portal.track.emailLabel': { am: 'በጥያቄው ላይ ያለ ኢሜይል', en: 'Email on the request' },
  'portal.track.lookupButton': { am: 'ፈልግ', en: 'Look up' },
  'portal.track.haveAccountPrompt': { am: 'መለያ አለዎት?', en: 'Have an account?' },
  'portal.track.myRequestsTitle': { am: 'የእኔ ጥያቄዎች', en: 'My requests' },
  'portal.track.myRequestsLede': {
    am: 'ለድርጅትዎ የቀረቡ ሁሉም ጥያቄዎች። ሁኔታውን፣ ሂደቱን እና ፕሮፎርማ ማውረጃውን ለማየት አንዱን ይምረጡ።',
    en: 'Every request submitted for your organization. Open one for its status, timeline, and proforma download.',
  },
  'portal.track.backToList': { am: '← ወደ ጥያቄዎቼ ተመለስ', en: '← Back to my requests' },
};

export default strings;
